import React from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaEnvelope, FaHeadset, FaUserCheck, FaLock,
  FaCoins, FaUsers, FaChevronDown, FaArrowRight, FaGlobe
} from "react-icons/fa";
import TopNavbar from "./TopNavbar";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function ContactUs() {
  const navigate = useNavigate();

  const faqs = [
    { icon: FaUserCheck, q: "How do I create an account?", a: "Navigate to the register page, fill in your details, and verify your email via the secure code sent to your inbox." },
    { icon: FaLock, q: "Login issues?", a: "Ensure your email is verified. Check your network or clear browser cache. If persistent, reach out with a screenshot of the error." },
    { icon: FaCoins, q: "How are profits credited?", a: "ROI is credited daily to your ledger after automated system verification of your active plan tasks." },
    { icon: FaUsers, q: "Referral program details?", a: "Share your unique link from the dashboard. Rewards are instantly credited once your referral activates a plan." },
    { icon: FaHeadset, q: "Support response time?", a: "Our global team typically responds within 24 hours. For priority handling, include your registered UID." },
  ];

  return (
    <>
      <TopNavbar />
      <div className="min-h-screen w-full bg-slate-950 text-slate-100 overflow-x-hidden font-sans pt-20 sm:pt-24 selection:bg-amber-500 selection:text-slate-950">
        
        {/* Dynamic Matrix Background */}
        <div className="fixed inset-0 z-0 pointer-events-none opacity-60">
          <div className="absolute top-0 right-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-blue-900/10 blur-[100px] md:blur-[140px] rounded-full animate-pulse" />
          <div className="absolute bottom-0 left-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-slate-900/50 blur-[100px] md:blur-[120px] rounded-full" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(251,191,36,0.01)_1px,transparent_1px),linear-gradient(rgba(251,191,36,0.01)_1px,transparent_1px)] bg-[size:40px_40px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-20 space-y-16 md:space-y-24">
          
          {/* HERO SECTION */}
          <motion.section 
            initial="hidden" animate="show" variants={fadeUp}
            className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center mt-6 lg:mt-0"
          >
            <div className="space-y-6 md:space-y-8 text-center lg:text-left flex flex-col items-center lg:items-start">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-amber-400 text-[10px] font-black uppercase tracking-widest shadow-sm">
                Support Protocol
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-none text-white uppercase">
                Get in touch <br className="hidden md:block" />
                <span className="text-amber-400">with 3Workers </span>
              </h1>
              <p className="text-slate-400 text-base md:text-lg max-w-md leading-relaxed font-medium">
                Experience seamless assistance. Whether it's a technical query or a partnership interest, our global team is ready.
              </p>
              
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-center justify-center lg:justify-start w-full sm:w-auto">
                <button 
                  onClick={() => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" })}
                  className="w-full sm:w-auto justify-center px-8 py-4 rounded-xl bg-amber-400 text-slate-950 font-black text-[11px] uppercase tracking-widest shadow-xl shadow-amber-500/10 hover:bg-white transition-all flex items-center gap-2 group"
                >
                  Read FAQs <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform shrink-0" />
                </button>
                <div className="flex gap-2 w-full sm:w-auto justify-center">
                   <MiniKpi label="Uptime" value="99.9%" />
                   <MiniKpi label="Avg Reply" value="~2h" />
                </div>
              </div>
            </div>

            <TiltSupportCard />
          </motion.section>

          {/* GLOBAL PRESENCE */}
          <motion.section 
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="space-y-8 md:space-y-12"
          >
            <div className="text-center lg:text-left border-b border-slate-900 pb-8 md:pb-10">
              <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight">Regional Support Nodes</h2>
              <p className="text-slate-400 mt-2 text-sm md:text-base font-medium">Connect with our localized infrastructure endpoints across the globe.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {[
                { name: "Germany", code: "de", email: "germany@3Workers" },
                { name: "Turkey", code: "tr", email: "turkey@3Workers" },
                { name: "UK", code: "gb", email: "london@3Workers" },
                { name: "Indonesia", code: "id", email: "asia@3Workers" },
                { name: "Russia", code: "ru", email: "russia@3Workers" },
              ].map((loc, i) => (
                <LocalContactCard key={i} loc={loc} />
              ))}
            </div>
          </motion.section>

          {/* FAQ SECTION */}
          <motion.section id="faq" className="bg-slate-900 rounded-[2rem] md:rounded-[3rem] p-6 md:p-10 lg:p-16 border border-slate-800 shadow-2xl">
             <h2 className="text-2xl md:text-4xl font-black mb-8 md:mb-12 text-center lg:text-left text-white uppercase tracking-tighter">Frequently Asked Terminal Core</h2>
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {faqs.map((f, i) => (
                  <FaqCard key={i} {...f} />
                ))}
             </div>
          </motion.section>

        </div>

        {/* FOOTER */}
        <footer className="bg-slate-950 border-t border-slate-900 py-12 md:py-16 mt-8">
           <div className="max-w-7xl mx-auto px-6 flex flex-col items-center space-y-6 md:space-y-8 text-center">
               <div className="text-xl md:text-2xl font-black tracking-tighter text-white">
                  <span className="text-amber-400">3</span>Workers
               </div>
               <nav className="flex flex-wrap justify-center gap-6 md:gap-8 text-xs md:text-sm font-bold text-slate-400">
                  <Link to="/" className="hover:text-amber-400 transition-colors">Dashboard</Link>
                  <Link to="/plans" className="hover:text-amber-400 transition-colors">Plans</Link>
                  <Link to="/contact" className="hover:text-amber-400 transition-colors">Support</Link>
               </nav>
               <div className="text-[10px] md:text-xs text-slate-500 font-medium">
                 © {new Date().getFullYear()} 3Workers.cc  | All Rights Reserved
               </div>
           </div>
        </footer>
      </div>
    </>
  );
}

function TiltSupportCard() {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [10, -10]);
  const rotateY = useTransform(x, [-100, 100], [-10, 10]);

  return (
    <motion.div 
      style={{ perspective: 1000 }}
      onMouseMove={(e) => {
        // Will safely fall back to neutral state on touch screens
        const rect = e.currentTarget.getBoundingClientRect();
        x.set(e.clientX - rect.left - rect.width/2);
        y.set(e.clientY - rect.top - rect.height/2);
      }}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      className="relative [transform-style:preserve-3d] w-full max-w-md mx-auto lg:max-w-none"
    >
      <motion.div 
        style={{ rotateX, rotateY }} 
        className="bg-slate-900 border border-slate-800 rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-10 shadow-2xl transition-shadow duration-300 hover:shadow-amber-500/5 text-left"
      >
        <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-amber-400 text-slate-950 flex items-center justify-center text-xl md:text-2xl shadow-xl shadow-amber-500/10 mb-6 md:mb-8">
          <FaHeadset />
        </div>
        <h3 className="text-xl md:text-2xl font-black mb-3 md:mb-4 text-white uppercase tracking-tight">Direct Node Dispatch</h3>
        <p className="text-slate-400 mb-6 md:mb-8 font-medium text-xs md:text-sm">For account recovery, verification parameters, and server status inquiries.</p>
        <a href="mailto:info@3workers.cx" className="group flex items-center justify-between p-4 md:p-5 rounded-xl md:rounded-2xl bg-slate-950 border border-slate-800 hover:border-amber-500/30 transition-all overflow-hidden gap-3">
          <span className="font-bold text-amber-400 font-mono tracking-wide text-sm md:text-base truncate">info@3workers.cx</span>
          <FaEnvelope className="text-slate-600 group-hover:text-amber-400 transition-colors shrink-0" />
        </a>
      </motion.div>
    </motion.div>
  );
}

function LocalContactCard({ loc }) {
  return (
    <motion.div 
      whileHover={{ y: -8, scale: 1.02 }}
      className="bg-slate-900 border border-slate-800 p-4 md:p-6 rounded-[1.5rem] md:rounded-[2rem] text-center shadow-md hover:shadow-2xl hover:border-amber-400/30 transition-all group"
    >
      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden mx-auto mb-3 md:mb-4 border-2 border-slate-800 group-hover:border-amber-400/40 transition-all">
        <img src={`https://flagcdn.com/w160/${loc.code}.png`} className="w-full h-full object-cover scale-110 grayscale group-hover:grayscale-0 transition-all duration-300" alt={loc.name} />
      </div>
      <h4 className="text-xs md:text-sm font-black text-slate-200 uppercase tracking-tight truncate">{loc.name}</h4>
      <p className="text-[9px] md:text-[10px] text-slate-500 mt-1 md:mt-2 truncate font-mono group-hover:text-amber-400 transition-colors">{loc.email}</p>
    </motion.div>
  );
}

function FaqCard({ icon: Icon, q, a }) {
  return (
    <details className="group bg-slate-950 border border-slate-800 rounded-2xl md:rounded-3xl p-5 md:p-6 transition-all hover:border-slate-700/50 hover:shadow-xl">
      <summary className="list-none cursor-pointer flex items-center justify-between selection:bg-transparent">
        <div className="flex items-center gap-3 md:gap-4 w-[90%]">
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-slate-900 border border-slate-800 text-amber-400 flex items-center justify-center shrink-0 group-open:bg-amber-400 group-open:text-slate-950 transition-colors">
            <Icon className="text-xs md:text-sm" />
          </div>
          <span className="font-bold text-slate-200 text-sm md:text-base tracking-tight transition-colors group-hover:text-white leading-tight">{q}</span>
        </div>
        <FaChevronDown className="text-slate-500 group-open:rotate-180 transition-transform text-xs shrink-0" />
      </summary>
      <div className="mt-4 md:mt-5 pl-0 md:pl-14 text-xs md:text-sm text-slate-400 leading-relaxed font-medium md:border-l border-slate-800/80">
        {a}
      </div>
    </details>
  );
}

function MiniKpi({ label, value }) {
  return (
    <div className="px-3 md:px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl flex-1 text-center sm:text-left">
      <div className="text-[7px] md:text-[8px] uppercase font-black text-slate-500 tracking-widest">{label}</div>
      <div className="text-xs md:text-sm font-mono font-black text-amber-400">{value}</div>
    </div>
  );
}